import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '@env';

import {
  CoverageDecisionPayload,
  CoverageRequestDetail,
  CoverageResponse,
  CoverageRule,
  CoverageSettings,
  CoverageSettingsPayload,
  CreateCoverageRequestPayload,
  DeleteCoverageRuleResponse,
  GetCoverageParams,
  ListCoverageRequestsParams,
  ListCoverageRequestsResponse,
  SaveCoverageRulePayload,
} from '../../../interfaces/coverage.interface';
import { QueryBuilder } from '../query-builder';
import { CoverageApiService } from './coverage-api.service';

/** Cliente de la API de cobertura del cotizador. */
@Injectable()
export class CoverageHttpService extends CoverageApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.salesApiGateway}quotes`;

  getSettings(): Observable<CoverageSettings> {
    return this.http.get<CoverageSettings>(`${this.baseUrl}/settings/coverage`);
  }

  updateSettings(body: CoverageSettingsPayload): Observable<CoverageSettings> {
    return this.http.put<CoverageSettings>(`${this.baseUrl}/settings/coverage`, body);
  }

  list(params: GetCoverageParams): Observable<CoverageResponse> {
    if (params.search) {
      const url = QueryBuilder.fromParams(`${this.baseUrl}/coverage`, {
        filters: { search: params.search },
      });
      return this.http.get<CoverageResponse>(url);
    }
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') {
        httpParams = httpParams.set(key, value);
      }
    }
    return this.http.get<CoverageResponse>(`${this.baseUrl}/coverage`, { params: httpParams });
  }

  saveRule(body: SaveCoverageRulePayload): Observable<CoverageRule> {
    return this.http.put<CoverageRule>(`${this.baseUrl}/coverage/rules`, body);
  }

  deleteRule(id: number, reason: string): Observable<DeleteCoverageRuleResponse> {
    return this.http.delete<DeleteCoverageRuleResponse>(`${this.baseUrl}/coverage/rules/${id}`, {
      body: { reason },
    });
  }

  createRequest(body: CreateCoverageRequestPayload): Observable<CoverageRequestDetail> {
    return this.http.post<CoverageRequestDetail>(`${this.baseUrl}/coverage/requests`, body);
  }

  listRequests(params: ListCoverageRequestsParams): Observable<ListCoverageRequestsResponse> {
    let httpParams = new HttpParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === false) continue;
      httpParams = httpParams.set(key, value === true ? 1 : value);
    }
    return this.http.get<ListCoverageRequestsResponse>(`${this.baseUrl}/coverage/requests`, {
      params: httpParams,
    });
  }

  getRequest(id: number): Observable<CoverageRequestDetail> {
    return this.http.get<CoverageRequestDetail>(`${this.baseUrl}/coverage/requests/${id}`);
  }

  decideRequest(id: number, body: CoverageDecisionPayload): Observable<CoverageRequestDetail> {
    return this.http.post<CoverageRequestDetail>(
      `${this.baseUrl}/coverage/requests/${id}/decision`,
      body,
    );
  }
}
